import * as React from 'react';

import { cn } from '@/ux-common/lib/utils';

/**
 * Button component - Minimal React wrapper for behavioral concerns.
 *
 * All visual styling is applied via CSS classes. This wrapper provides:
 * - `type="button"` default (prevents form submission bugs)
 * - `data-component="Button"` for component identification
 * - Ref forwarding for HOC/tooltip compatibility
 * - `className` merging via `cn()`
 *
 * The `.btn` base class is applied automatically — `className` composes
 * modifiers and utilities on top of it.
 *
 * @example
 * ```tsx
 * // Default button (gets .btn .btn-default)
 * <Button>Save</Button>
 *
 * // Variant + size + shape
 * <Button className="btn-tier2 btn-sm btn-circle" aria-label="Close">
 *   <XIcon className="btn-icon" />
 * </Button>
 *
 * // Full-width brand button
 * <Button className="btn-brand btn-lg w-full">Submit</Button>
 * ```
 */

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, type = 'button', children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        data-component="Button"
        className={cn('btn', className)}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
